/**
 * Content Studio module API client — fetchApi wrapper.
 *
 * All Content Studio backend controllers live under `/v1/content-studio/*`
 * (decks, palettes; preview/sessions land in later waves). `csFetch` enforces
 * the `/content-studio/` prefix (fetchApi contributes the `/v1` version
 * prefix + base URL) so callers cannot accidentally encode `/v1/`, `/api/`,
 * or omit the module prefix. Auth headers (Bearer token + `x-workspace-id`)
 * are injected automatically by `fetchApi` — tenant isolation rides in that
 * header; content-studio query keys carry NO workspaceId and NO mode (see
 * components/content-studio/hooks/query-keys.ts).
 *
 * Mirrors the `lmsFetch()` / `esaFetch()` wrapper convention.
 */
import {
  ApiClientError,
  fetchApi,
  getAccessToken,
  getWorkspaceId,
} from '@/lib/api/client'
import { API_CONFIG } from '@/lib/api/config'

export { ApiClientError as CsApiError } from '@/lib/api/client'

/**
 * Content Studio JSON fetch. Caller passes the path AFTER `/content-studio/`
 * (e.g. `decks`, `decks/${id}/duplicate`, `palettes`).
 */
export async function csFetch<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const cleanPath = path.replace(/^\/+/, '')
  const endpoint = `/content-studio/${cleanPath}`
  return fetchApi<T>(endpoint, options)
}

/**
 * Content Studio TEXT fetch — for the non-JSON routes (the deck preview
 * returns `text/html`; Task 12 contract: GET `decks/:id/preview`).
 *
 * `fetchApi` cannot serve this: it force-parses JSON and returns `undefined`
 * for any other content type, so the preview body would silently vanish.
 * Same auth surface (Bearer + x-workspace-id), same `/content-studio/`
 * prefix enforcement, same ApiClientError on non-2xx. No 401 auto-refresh —
 * the page's JSON queries (via fetchApi) own session recovery; a preview 401
 * surfaces as a retryable error pane instead.
 */
export async function csFetchText(
  path: string,
  options?: RequestInit,
): Promise<string> {
  const cleanPath = path.replace(/^\/+/, '')
  const url = `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}/content-studio/${cleanPath}`

  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string>),
  }
  const token = getAccessToken()
  if (token) headers.Authorization = `Bearer ${token}`
  const workspaceId = getWorkspaceId()
  if (workspaceId) headers['x-workspace-id'] = workspaceId

  const response = await fetch(url, { ...options, headers })
  if (!response.ok) {
    let body: unknown
    try {
      body = await response.json()
    } catch {
      body = undefined
    }
    throw new ApiClientError(response.status, response.statusText, body)
  }
  return response.text()
}

/**
 * Content Studio MULTIPART fetch — for the routes that take a file.
 *
 * `fetchApi` cannot serve this either, for the opposite reason to
 * `csFetchText`: it force-sets `Content-Type: application/json`, which
 * DESTROYS the multipart boundary the browser would otherwise write (the
 * exact reason `lib/content-studio/upload.ts` and `lib/editor/api.ts` each
 * roll their own). So the body goes out as a bare `FormData` with NO
 * Content-Type header set by hand — the browser fills it in, boundary and all.
 *
 * Same auth surface (Bearer + x-workspace-id), same `/content-studio/` prefix
 * enforcement, same `ApiClientError` on non-2xx — which matters here: the
 * document classifier answers an unusable upload with a 400 whose `message`
 * is the sentence the author is meant to read, and `ApiClientError` lifts
 * exactly that into `.message`.
 */
export async function csFetchMultipart<T>(
  path: string,
  body: FormData,
): Promise<T> {
  const cleanPath = path.replace(/^\/+/, '')
  const url = `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}/content-studio/${cleanPath}`

  const headers: Record<string, string> = {}
  const token = getAccessToken()
  if (token) headers.Authorization = `Bearer ${token}`
  const workspaceId = getWorkspaceId()
  if (workspaceId) headers['x-workspace-id'] = workspaceId

  const response = await fetch(url, { method: 'POST', headers, body })

  if (!response.ok) {
    // `.text()` after a failed `.json()` rejects ("body already read"), so the
    // fallback is caught rather than allowed to replace a real HTTP failure
    // with a confusing stream error.
    let errorBody: unknown
    try {
      errorBody = await response.json()
    } catch {
      errorBody = await response.text().catch(() => undefined)
    }
    throw new ApiClientError(response.status, response.statusText, errorBody)
  }

  return (await response.json()) as T
}

/**
 * Query-string builder for content-studio list endpoints. Skips
 * undefined/null/empty values; ARRAY values are appended as repeated params
 * (`status=Draft&status=Ready`) — the BE `ListDecksQueryDto` @Transform
 * accepts both the single-value and the repeated-array wire shape.
 */
export function csQueryString(
  filters?: Record<
    string,
    string | number | boolean | readonly (string | number)[] | undefined | null
  >,
): string {
  if (!filters) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      for (const v of value) params.append(key, String(v))
    } else {
      params.set(key, String(value))
    }
  }
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}
